import { useEffect, useCallback, useMemo } from 'react';

import { useCurrentProjectId } from '@adsk/alloy-provider';

import useMemoizedStringArray from './useMemoizedStringArray';
import usePendingIds from './usePendingIds';
import { useEntitiesCacheContext } from '../providers/EntitiesCacheProvider';

const PAGE_SIZE = 100;

export default (plugin, idsArg) => {
  const { entitiesCache, addEntitiesToCache, addLoadingIds, addErrorIds } =
    useEntitiesCacheContext();
  const projectId = useCurrentProjectId();
  const fetchedEntityIds = useMemo(() => {
    const { type, domain } = plugin.metadata;
    const cache = entitiesCache?.[domain]?.[type];
    return cache ? Object.keys(cache) : [];
  }, [plugin, entitiesCache]);
  const memoizedFetchedEntityIds = useMemoizedStringArray(fetchedEntityIds);
  const {
    pendingIdsArray,
    addPendingIds,
    addAlreadyFetchedIds,
    clearPendingIds,
  } = usePendingIds();

  useEffect(() => {
    if (memoizedFetchedEntityIds) {
      addAlreadyFetchedIds(memoizedFetchedEntityIds);
    }
  }, [addAlreadyFetchedIds, memoizedFetchedEntityIds]);

  const memoizedIds = useMemoizedStringArray(idsArg);
  useEffect(() => {
    addPendingIds(memoizedIds);
  }, [addPendingIds, memoizedIds]);

  const readData = useCallback(
    (
      projectId,
      plugin,
      ids,
      addLoadingIds,
      addEntitiesToCache,
      addErrorIds,
    ) => {
      if (ids.length) {
        const { type, domain } = plugin.metadata;
        addLoadingIds({ ids, domain, type });
        plugin
          .getEntitiesByIds({ ids, projectId })
          .then(({ data: entities }) => {
            addEntitiesToCache({ entities, requestIds: ids, domain, type });
          })
          .catch((error) => {
            console.error(
              'ReferencePicker - useGetEntities: Error getting entities',
              error,
            );
            // This is needed as we hold error per entity, and we might need to trigger retry for multiple entities.
            // So here we mark whether the retry method was called or not.
            let retryCalled = false;
            addErrorIds({
              ids,
              domain,
              type,
              error,
              retry: () => {
                if (!retryCalled) {
                  readData(
                    projectId,
                    plugin,
                    ids,
                    addLoadingIds,
                    addEntitiesToCache,
                    addErrorIds,
                  );
                  retryCalled = true;
                }
              },
            });
          });
      }
    },
    [],
  );

  useEffect(() => {
    if (projectId && pendingIdsArray.length) {
      for (let i = 0; i < pendingIdsArray.length; i += PAGE_SIZE) {
        const chunkIds = pendingIdsArray.slice(i, i + PAGE_SIZE);
        readData(
          projectId,
          plugin,
          chunkIds,
          addLoadingIds,
          addEntitiesToCache,
          addErrorIds,
        );
      }
      clearPendingIds();
    }
  }, [
    readData,
    pendingIdsArray,
    plugin,
    projectId,
    addLoadingIds,
    addEntitiesToCache,
    addErrorIds,
    clearPendingIds,
  ]);

  return {
    fetchByIds: addPendingIds,
  };
};
